import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  LabraToolTipV2,
  TextArea,
  TextInputV2,
} from '@labrav/react-components'
import React, { useMemo, useState } from 'react'
import { faPhone, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { useStyles } from './FlyOutProductDetailSupportInformation.styles'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { getRandom } from '../../../common/utils/helperFunctions'
import { RequirementLink } from '../../../types/RequirementLink'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { FlyoutProductDetailsSupportInformationFaqs } from './FlyoutProductDetailsSupportInformationFaqs'

type FlyOutProductDetailSupportInformationProps = {
  handleRefLinkClick?: (inex: number) => void
  detailView?: boolean
  detailViewTooltip?: string
}
export const FlyOutProductDetailSupportInformation: React.FC<
  RequirementLink & FormLockedType & FlyOutProductDetailSupportInformationProps
> = ({ isFormLocked, handleRefLinkClick, detailView, detailViewTooltip }) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, handleChange } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation
  const resourceUrls = generalInformation?.resourceUrls || []
  const randomKey = useMemo(() => getRandom(), [resourceUrls.length])
  const emptyResourse = { Text: '', Url: '' }
  const isReadOnlyValue = !!isFormLocked || !!detailView
  const [indexModal, setIndexModal] = useState<number>(1)

  return (
    <BoxV2
      heading={'Support information'}
      headingIcon={
        <FontAwesomeIcon
          icon={faPhone}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductDetailsSupportInformationFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                Support details
              </Typography>
            </div>
            <Typography className={classes.sectionText}>
              Describe the level of support that buyers can expect when they
              purchase your product, including contact information and links to
              any support channels you provide. This information is displayed on
              the product listing page under ‘Support information’. (up to 2,000
              characters max).{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(1)}
              >
                (Refer no. 8 in image no. 2)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.fullWidth}
          >
            <TextArea
              keyName="generalInformation.supportResources"
              title="Enter support details for your product"
              rows={10}
              maxCharacters={2000}
              height={80}
              type="string"
              placeHolder=""
              value={generalInformation?.supportResources}
              updatedValue={generalInformation?.supportResources}
              isError={
                !!(
                  generalInformationTouched?.supportResources &&
                  generalInformationErrors?.supportResources
                )
              }
              onChange={handleChange}
              isReadOnly={isFormLocked as boolean}
              isRequired={false}
              errorText={
                (!!generalInformationTouched?.supportResources &&
                  generalInformationErrors?.supportResources) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
              viewOnly={detailView}
            />
          </LabraToolTipV2>
        </div>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>
                Learning resources
              </Typography>
              <Typography className={classes.sectionText}>Optional</Typography>
            </div>
            <Typography className={classes.sectionText}>
              Provide links to any resources you’ve created to help buyers learn
              more about your product, such as product sheets or white papers.
              Resource links are displayed to buyers on the product listing
              page.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(1)}
              >
                (Refer no. 9 in image no. 2)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.resourcesContainerDisable}
          >
            <FieldArray name="generalInformation.resourceUrls">
              {({ push, remove }) => (
                <>
                  {(isEmpty(resourceUrls)
                    ? [{ Text: '', Url: '' }]
                    : resourceUrls
                  ).map((resource, index) => (
                    <div
                      className={classes.resourceRow}
                      data-testid="resource-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={classes.resouceField}
                          key={`resource-${randomKey}-name`}
                        >
                          <TextInputV2
                            keyName={`generalInformation.resourceUrls.${index}.Text`}
                            title="Resource Name"
                            type="string"
                            maxCharacters={500}
                            placeHolder=""
                            value={resource.Text}
                            updatedValue={resource.Text}
                            isError={
                              !!(
                                get(
                                  generalInformationTouched,
                                  `resourceUrls.${[index]}.Text`,
                                  false
                                ) &&
                                get(
                                  generalInformationErrors,
                                  `resourceUrls.${[index]}.Text`,
                                  false
                                )
                              )
                            }
                            onChange={handleChange}
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            errorText={
                              (!!get(
                                generalInformationTouched,
                                `resourceUrls.${[index]}.Text`,
                                false
                              ) &&
                                get(
                                  generalInformationErrors,
                                  `resourceUrls.${[index]}.Text`,
                                  ' '
                                )) ||
                              ' '
                            }
                            showSpinner={false}
                            onBlur={handleBlur}
                            viewOnly={detailView}
                          />
                        </div>
                        <div
                          className={classes.resouceField}
                          key={`resource-${randomKey}-Url`}
                        >
                          <TextInputV2
                            keyName={`generalInformation.resourceUrls.${index}.Url`}
                            title="Resource URL"
                            type="string"
                            placeHolder=""
                            value={resource.Url as string}
                            updatedValue={resource.Url as string}
                            isError={
                              !!(
                                get(
                                  generalInformationTouched,
                                  `resourceUrls.${[index]}.Url`,
                                  false
                                ) &&
                                get(
                                  generalInformationErrors,
                                  `resourceUrls.${[index]}.Url`,
                                  false
                                )
                              )
                            }
                            onChange={handleChange}
                            isReadOnly={isFormLocked as boolean}
                            isRequired={false}
                            errorText={
                              (!!get(
                                generalInformationTouched,
                                `resourceUrls.${[index]}.Url`,
                                false
                              ) &&
                                get(
                                  generalInformationErrors,
                                  `resourceUrls.${[index]}.Url`,
                                  ''
                                )) ||
                              ''
                            }
                            showSpinner={false}
                            onBlur={handleBlur}
                            viewOnly={detailView}
                          />
                        </div>
                      </div>
                      {resourceUrls.length > 1 ? (
                        <div className={classes.trashIcon}>
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ width: '18px', height: '20px' }}
                            data-testid="trash-resource"
                            onClick={() => remove(index)}
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  ))}
                  {resourceUrls.length <= 2 ? (
                    <ButtonV2
                      styleType="link"
                      onClick={() => push(emptyResourse)}
                      data-testid="add-a-resouce-button"
                      disabled={isReadOnlyValue}
                    >
                      Add a resource
                    </ButtonV2>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </FieldArray>
          </LabraToolTipV2>
        </div>
      </>
    </BoxV2>
  )
}
